import i18n from "../../i18n";
import React, { useEffect, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import ReactJson from "react-json-view";
import { UserCard } from "../../components/consultantList";
import { Page } from "../../layout/page";
import _ from "lodash";
import { FormSectionCard } from "../../layout/page";
import { useDisclosure } from "@mantine/hooks";

import {
  Badge,
  Box,
  Grid,
  Group,
  Text,
  UnstyledButton,
  Drawer,
  Divider,
  ScrollArea,
  Modal,
} from "@mantine/core";
import { useServerApi } from "../../hooks/userServerApi";
import { useFormRender } from "../../hooks/useFormRender";
import { Tabs } from "@mantine/core";

import ForcastPayrollBarChart from "../../components/forcastPayrollBarChart";
import OrderStatics from "../../components/orderStatics";

import MyTeamMemberDetailPage from "../../components/myTeamMemberDetailPage";
import UserOrderList from "../../components/userOrderList";
import UserOrderHistory from "../../components/userOrderHistory";
import UserClientList from "../../components/userClientList";

const MY_TEAM_UPLINE_LEVELS = (() => {
  const raw = process.env.REACT_APP_MY_TEAM_UPLINE_LEVELS;
  const parsed = raw != null && raw !== "" ? parseInt(raw, 10) : 5;
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 5;
})();

const TeamMemberDetailPage = ({ memberId }) => {
  const [member, setMember] = useState(null);
  const [api] = useServerApi();

  const fetchMemberDetail = async () => {
    const data = await api.getById({ apiEntity: "user", id: memberId });
    setMember(data);
  };

  useEffect(() => {
    fetchMemberDetail();
  }, [memberId]);

  return (
    <>
      {member && (
        <>
          <Divider mb={"xl"} />
          <UserCard value={member} mb={"xl"} />

          <Tabs defaultValue="stats">
            <Tabs.List>
              <Tabs.Tab value="stats">{i18n.t("Order Statistic")}</Tabs.Tab>
              <Tabs.Tab value="order">{i18n.t("Orders")}</Tabs.Tab>
              <Tabs.Tab value="client">{i18n.t("Clients")}</Tabs.Tab>
              <Tabs.Tab value="forecast">{i18n.t("Payroll Forecast")}</Tabs.Tab>
              <Tabs.Tab value="personal">{i18n.t("Personal Info")}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="stats" pt="xl">
              <OrderStatics userId={memberId} />
            </Tabs.Panel>

            <Tabs.Panel value="order" pt="xl">
              <UserOrderHistory id={memberId} />
            </Tabs.Panel>
            <Tabs.Panel value="client" pt="xl">
              <UserClientList userId={memberId} />
            </Tabs.Panel>

            <Tabs.Panel value="forecast" pt="xl">
              <ForcastPayrollBarChart memberId={memberId} />
            </Tabs.Panel>

            <Tabs.Panel value="personal" pt="xl">
              <MyTeamMemberDetailPage memberId={memberId} />
            </Tabs.Panel>
          </Tabs>
        </>
      )}
    </>
  );
};

const Me = ({ handleMemberSelect, ...props }) => {
  const auth = useAuthUser();

  return (
    <UnstyledButton onClick={() => handleMemberSelect(auth().id)}>
      <Box {...props}>
        <Text transform="uppercase" size="xs" mb="sm">
          Team Lead (Me)
        </Text>
        <UserCard value={auth()}></UserCard>
      </Box>
    </UnstyledButton>
  );
};
const TeamLevelMembers = ({
  teamMemberLevel,
  handleMemberSelect,
  levelLabelPrefix = "Level",
  clickable = true,
}) => {
  const { members, level } = teamMemberLevel;

  return (
    teamMemberLevel && (
      <Box mb={60}>
        <Group mb="lg">
          <Text transform="uppercase" size="xs">
            {levelLabelPrefix} {level}
          </Text>
          <Badge size="xs">{members?.length ?? 0}</Badge>
        </Group>

        <Grid columns={3}>
          {members.map((m) => (
            <Grid.Col key={m._id} span={1}>
              {clickable ? (
                <UnstyledButton onClick={() => handleMemberSelect(m?._id)}>
                  <UserCard value={m} mr="xl" mb="xl" />
                </UnstyledButton>
              ) : (
                <UserCard value={m} mr="xl" mb="xl" />
              )}
            </Grid.Col>
          ))}
        </Grid>
      </Box>
    )
  );
};

const UplineMembers = ({
  numLevel = MY_TEAM_UPLINE_LEVELS,
  userId,
}) => {
  const [uplineMembers, setUplineMembers] = useState([]);
  const [api] = useServerApi();

  useEffect(() => {
    const fetchUpline = async () => {
      if (!userId || numLevel < 1) return;
      const data = await api.User.getUplineMembers(userId, numLevel);
      setUplineMembers(data ?? []);
    };
    fetchUpline();
  }, [userId, numLevel]);

  if (!uplineMembers?.length) {
    return (
      <Text color="dimmed" size="sm">
        No upline to display.
      </Text>
    );
  }

  return (
    <>
      {[...uplineMembers].reverse().map((tm, i) => (
        <TeamLevelMembers
          key={`upline-${tm.level}-${i}`}
          teamMemberLevel={tm}
          levelLabelPrefix="Upline Level"
          clickable={false}
        />
      ))}
    </>
  );
};

const LevelMembers = ({ numLevel = 2, teamLeadId, handleMemberSelect }) => {
  const [teamMembers, setTeamMembers] = useState([]);

  const [api] = useServerApi();

  const fetchTeamMembers = async (teamLeadId, numLevel) => {
    if (!teamLeadId || numLevel < 1) return;
    const data = await api.User.getTeamMembers(teamLeadId, numLevel);
    setTeamMembers(data);
    // console.log("Members", data);
  };

  useEffect(() => {
    fetchTeamMembers(teamLeadId, numLevel);
  }, [teamLeadId]);

  return (
    <>
      {teamMembers?.map((tm, i) => (
        <TeamLevelMembers
          key={i}
          teamMemberLevel={tm}
          handleMemberSelect={handleMemberSelect}
        />
      ))}
    </>
  );
};

const MyTeam = ({}) => {
  const title = "My Team";
  const [api] = useServerApi();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const auth = useAuthUser();

  const handleMemberSelect = async (memberId) => {
    // console.log("member click", memberId);
    setSelectedMember(memberId);
    // await api.User.getTeamMemberDetail();
    open();
  };

  return (
    <Page title={title}>
      <Modal
        opened={opened}
        onClose={close}
        title="Team Member Details"
        position="right"
        size="1000px"
        padding={"xl"}
      >
        <TeamMemberDetailPage memberId={selectedMember} />
      </Modal>

      <Tabs defaultValue="team">
        <Tabs.List>
          <Tabs.Tab value="team">{i18n.t("My Team")}</Tabs.Tab>
          <Tabs.Tab value="upline">{i18n.t("Upline")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="team" pt="xl">
          <Me mb={60} handleMemberSelect={handleMemberSelect} />
          <LevelMembers
            numLevel={2}
            teamLeadId={auth().id}
            handleMemberSelect={handleMemberSelect}
          />
        </Tabs.Panel>

        <Tabs.Panel value="upline" pt="xl">
          <UplineMembers
            userId={auth().id}
            numLevel={MY_TEAM_UPLINE_LEVELS}
          />
        </Tabs.Panel>
      </Tabs>
    </Page>
  );
};

export default MyTeam;
